import { getHeadersFiles } from "./middleware";
import { handleServiceError } from "./errorHandler";
import { REACT_APP_BACKEND_API } from "../config";
import logger from '../utils/logger';
import { sentryFetch, createApiHeaders } from "./sentryApiWrapper";

const BASE_URL = REACT_APP_BACKEND_API + 'files';

// Segmento de carpeta para clientes entre región y quoteID
export const CLIENTS_FOLDER_SEGMENT = '/clients';

export interface FileUploadRequest {
    quoteID: string;
    groupID: string;
    questionID: string;
    region: string;
    files: File[];
}

export interface FileUploadResponse {
    message: string;
    data: {
        files: {
            id: string;
            name: string;
            path: string;
        }[];
        paths: string;
    };
}

/**
 * Uploads multiple files to the server
 * @param request The file upload request containing quoteID, groupID, questionID and files
 * @returns A promise with the upload response
 */
export const uploadFiles = async (request: FileUploadRequest): Promise<FileUploadResponse> => {
    try {
        logger.log('[fileService]', 'Starting file upload:', {
            quoteID: request.quoteID,
            groupID: request.groupID,
            questionID: request.questionID,
            fileCount: request.files.length,
            region: request.region
        });

        const formData = new FormData();

        // Add each file to the form data (backend will remove extension)
        request.files.forEach((file, index) => {
            logger.log('[fileService]', `Adding file ${index + 1}:`, {
                name: file.name,
                type: file.type,
                size: file.size
            });
            formData.append('files', file);
        });

        // Build the URL with query parameters (clients/quoteID/groupID/questionID en el param quoteID)
        const clientsSegmentNoSlash = CLIENTS_FOLDER_SEGMENT.replace(/^\//, '');
        const quoteIDWithClients = `${clientsSegmentNoSlash}/${request.quoteID}/${request.groupID}/${request.questionID}`;
        const url = `${BASE_URL}?quoteID=${encodeURIComponent(quoteIDWithClients)}&groupID=${encodeURIComponent(request.groupID)}&questionID=${encodeURIComponent(request.questionID)}&region=${encodeURIComponent(request.region)}`;
        logger.log('[fileService]', 'Request URL:', url);

        // Get headers but remove Content-Type as it will be set automatically for FormData
        const headers = getHeadersFiles();
        headers.delete('Content-Type');

        logger.log('[fileService]', 'Starting POST request with Sentry monitoring...');
        const response = await sentryFetch(
            url,
            {
                method: 'POST',
                headers: Object.fromEntries(headers.entries()),
                body: formData,
            },
            {
                operation: 'upload_files',
                quoteID: request.quoteID,
                groupID: request.groupID,
                questionID: request.questionID,
                fileCount: request.files.length,
                region: request.region,
                totalSize: request.files.reduce((sum, file) => sum + file.size, 0)
            }
        );

        if (!response.ok) {
            const errorData = await response.json();
            logger.error('[fileService]', 'Error uploading files:', {
                status: response.status,
                statusText: response.statusText,
                error: errorData
            });
            throw new Error(`Error: ${response.status} - ${errorData.message || 'Error uploading files'}`);
        }

        logger.log('[fileService]', 'File upload successful');
        const responseData = await response.json() as FileUploadResponse;
        logger.log('[fileService]', 'Server response:', responseData);
        return responseData;
    } catch (error) {
        logger.error('[fileService]', 'General error in uploadFiles:', error);
        handleServiceError(error);
        throw error;
    }
};

/**
 * Creates an initial file upload request with empty values
 * @returns An empty FileUploadRequest object
 */
export interface DeleteFolderRequest {
    path: string;
}

export interface DeleteFolderResponse {
    message: string;
    data: {
        path: string;
    };
}

/**
 * Elimina una carpeta en el servidor
 * @param request Objeto con la ruta de la carpeta a eliminar
 * @returns Promesa con la respuesta de eliminación
 */
export const deleteFolder = async (request: DeleteFolderRequest): Promise<DeleteFolderResponse> => {
    try {
        const url = `${BASE_URL}/folder?path=${encodeURIComponent(request.path)}`;

        const response = await sentryFetch(
            url,
            {
                method: 'DELETE',
                headers: getHeadersFiles(),
            },
            {
                operation: 'delete_folder',
                path: request.path
            }
        );

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(`Error: ${response.status} - ${errorData.message || 'Error eliminando carpeta'}`);
        }

        return await response.json() as DeleteFolderResponse;
    } catch (error) {
        handleServiceError(error);
        throw error;
    }
};

export const createInitialFileUploadRequest = (): FileUploadRequest => {
    return {
        quoteID: "",
        groupID: "",
        questionID: "",
        region: "",
        files: []
    };
};

export interface FileUploadSingleRequest {
    quoteID: string;
    region: string;
    file: File;
    /** Si es true, mantiene la extensión del archivo. Por defecto es false (quita la extensión). */
    keepExtension?: boolean;
}

export interface FileUploadSingleResponse {
    message: string;
    data: {
        file: {
            id: string;
            name: string;
            path: string;
        };
    };
}

/**
 * Uploads a single file to the server
 * @param request The file upload request containing quoteID, region, and file
 * @returns A promise with the upload response
 */
export const uploadFile = async (request: FileUploadSingleRequest): Promise<FileUploadSingleResponse> => {
    const SINGLE_FILE_ENDPOINT = REACT_APP_BACKEND_API + 'file';
    try {
        const keepExtension = request.keepExtension ?? false;
        logger.log('[fileService]', 'Starting single file upload:', {
            quoteID: request.quoteID,
            region: request.region,
            fileName: request.file.name,
            keepExtension: keepExtension,
            fileSize: request.file.size,
            fileType: request.file.type
        });

        const formData = new FormData();
        formData.append('file', request.file);

        const clientsSegmentNoSlash = CLIENTS_FOLDER_SEGMENT.replace(/^\//, '');
        const quoteIDWithClients = `${clientsSegmentNoSlash}/${request.quoteID}`;
        // Agregar keepExtension como query param solo si es true
        const keepExtensionParam = keepExtension ? '&keepExtension=true' : '';
        const url = `${SINGLE_FILE_ENDPOINT}?quoteID=${encodeURIComponent(quoteIDWithClients)}&region=${encodeURIComponent(request.region)}${keepExtensionParam}`;
        logger.log('[fileService]', 'Request URL (single file):', url);

        const headers = getHeadersFiles();
        headers.delete('Content-Type');

        logger.log('[fileService]', 'Starting POST request (single file) with Sentry monitoring...');
        const response = await sentryFetch(
            url,
            {
                method: 'POST',
                headers: Object.fromEntries(headers.entries()),
                body: formData,
            },
            {
                operation: 'upload_single_file',
                quoteID: request.quoteID,
                region: request.region,
                fileName: request.file.name,
                keepExtension: keepExtension,
                fileSize: request.file.size,
                fileType: request.file.type
            }
        );

        if (!response.ok) {
            const errorData = await response.json();
            logger.error('[fileService]', 'Error uploading single file:', {
                status: response.status,
                statusText: response.statusText,
                error: errorData
            });
            throw new Error(`Error: ${response.status} - ${errorData.message || 'Error uploading single file'}`);
        }

        logger.log('[fileService]', 'Single file upload successful');
        const responseData = await response.json() as FileUploadSingleResponse;
        logger.log('[fileService]', 'Server response (single file):', responseData);
        return responseData;
    } catch (error) {
        logger.error('[fileService]', 'General error in uploadFile:', error);
        handleServiceError(error);
        throw error;
    }
};

export interface FileExistsRequest {
    path: string;
}

export interface FileExistsResponse {
    message: string;
    data: {
        exists: boolean;
        // path: string; // Eliminado para que coincida con el backend
    };
}

/**
 * Verifica si un archivo existe en el servidor.
 * @param request Objeto con la ruta del archivo a verificar.
 * @returns Promesa con la respuesta sobre la existencia del archivo.
 */
export const checkFileExists = async (request: FileExistsRequest): Promise<FileExistsResponse> => {
    try {
        const url = `${BASE_URL}/exists?path=${encodeURIComponent(request.path)}`;
        logger.log('[fileService]', 'Checking file existence for path:', request.path);
        logger.log('[fileService]', 'Request URL for file existence check:', url);

        const response = await sentryFetch(
            url,
            {
                method: 'GET',
                headers: createApiHeaders(),
            },
            {
                operation: 'check_file_exists',
                path: request.path
            }
        );

        if (!response.ok) {
            let errorResponseMessage = 'Error checking file existence'; // Default message
            try {
                const errorData = await response.json();
                if (errorData && errorData.message) {
                    errorResponseMessage = errorData.message;
                }
            } catch (parseError) {
                // If parsing fails, use statusText or a generic message
                errorResponseMessage = response.statusText || 'Server returned an error without a JSON message';
                logger.warn('[fileService]', 'Could not parse error response as JSON for file existence check', parseError);
            }
            logger.error('[fileService]', 'Error checking file existence:', {
                status: response.status,
                statusText: response.statusText,
                messageFromServer: errorResponseMessage,
                path: request.path
            });
            throw new Error(`Error: ${response.status} - ${errorResponseMessage}`);
        }

        const responseData = await response.json() as FileExistsResponse;
        logger.log('[fileService]', 'File existence check successful. Server response:', responseData);
        return responseData;
    } catch (error) {
        logger.error('[fileService]', 'General error in checkFileExists:', error);
        handleServiceError(error);
        throw error;
    }
};

export interface DownloadFileRequest {
    path: string;
}

/**
 * Downloads a file from the server.
 * @param request Object with the path of the file to download.
 * @returns A promise with the raw fetch Response object.
 */
export const downloadFile = async (request: DownloadFileRequest): Promise<Response> => {
    try {
        const url = `${BASE_URL}/download?path=${encodeURIComponent(request.path)}`;
        logger.log('[fileService]', 'Downloading file from path:', request.path);
        logger.log('[fileService]', 'Request URL for file download:', url);

        const response = await sentryFetch(
            url,
            {
                method: 'GET',
                headers: getHeadersFiles(),
            },
            {
                operation: 'download_file',
                path: request.path
            }
        );

        if (!response.ok) {
            // Attempt to parse error data, but prioritize response.text() for download errors
            // as the body might not be JSON or could be the file content itself in some error scenarios.
            let errorResponseMessage = `Error downloading file: ${response.status} ${response.statusText}`;
            try {
                const errorData = await response.json(); // Try to parse as JSON first
                if (errorData && errorData.message) {
                    errorResponseMessage = errorData.message;
                }
            } catch (e) {
                // If JSON parsing fails, it might be a non-JSON error response or the file itself.
                // For downloads, often the raw response text or blob is more informative for debugging.
                // However, for consistency with other error handling:
                logger.warn('[fileService]', 'Could not parse error response as JSON for file download', e);
            }

            logger.error('[fileService]', 'Error downloading file:', {
                status: response.status,
                statusText: response.statusText,
                messageFromServer: errorResponseMessage, // This might be generic if parsing failed
                path: request.path,
                responseBodyAttempt: await response.text() // Log the raw text for more context
            });
            // Throw an error that includes the status and the parsed/raw message
            throw new Error(`Error: ${response.status} - ${errorResponseMessage}`);
        }

        logger.log('[fileService]', 'File download request successful. Returning raw response.');
        // For file downloads, we usually return the raw response object
        // so the caller can handle the stream, blob, etc.
        return response;
    } catch (error) {
        logger.error('[fileService]', 'General error in downloadFile:', error);
        handleServiceError(error); // Assuming this can handle the error or re-throw
        throw error; // Re-throw the error to be caught by the caller
    }
};

export interface FindFileWithoutExtensionRequest {
    baseFilename: string;
}

export interface FindFileWithoutExtensionResponse {
    message: string;
    data: {
        foundKey: string;
        baseFilename: string;
    };
    found: boolean;
}

/**
 * Busca un archivo sin extensión en el servidor.
 * @param request Objeto con el nombre base del archivo a buscar.
 * @returns Promesa con la respuesta que incluye la clave del archivo encontrado.
 */
export const findFileWithoutExtension = async (request: FindFileWithoutExtensionRequest): Promise<FindFileWithoutExtensionResponse> => {
    try {
        const url = `${BASE_URL}/find?baseFilename=${encodeURIComponent(request.baseFilename)}`;
        logger.log('[fileService]', 'Searching for file without extension:', request.baseFilename);
        logger.log('[fileService]', 'Request URL for file search:', url);

        const response = await sentryFetch(
            url,
            {
                method: 'GET',
                headers: getHeadersFiles(),
            },
            {
                operation: 'find_file_without_extension',
                baseFilename: request.baseFilename
            }
        );

        if (!response.ok) {
            let errorResponseMessage = 'Error searching for file without extension';
            try {
                const errorData = await response.json();
                if (errorData && errorData.message) {
                    errorResponseMessage = errorData.message;
                }
            } catch (parseError) {
                errorResponseMessage = response.statusText || 'Server returned an error without a JSON message';
                logger.warn('[fileService]', 'Could not parse error response as JSON for file search', parseError);
            }
            logger.error('[fileService]', 'Error searching for file without extension:', {
                status: response.status,
                statusText: response.statusText,
                messageFromServer: errorResponseMessage,
                baseFilename: request.baseFilename
            });
            throw new Error(`Error: ${response.status} - ${errorResponseMessage}`);
        }

        const responseData = await response.json() as FindFileWithoutExtensionResponse;
        
        // Verificar si el archivo fue encontrado para mantener compatibilidad hacia atrás
        if (responseData.found === false) {
            throw new Error(`File not found: ${request.baseFilename}`);
        }
        
        logger.log('[fileService]', 'File search successful. Server response:', responseData);
        return responseData;
    } catch (error) {
        logger.error('[fileService]', 'General error in findFileWithoutExtension:', error);
        handleServiceError(error);
        throw error;
    }
};

export interface DeleteFileRequest {
    path: string;
}

export interface DeleteFileResponse {
    message: string;
    data: {
        path: string;
    };
}

/**
 * Elimina un archivo específico en el servidor.
 * @param request Objeto con la ruta del archivo a eliminar.
 * @returns Promesa con la respuesta de eliminación del archivo.
 */
export const deleteFile = async (request: DeleteFileRequest): Promise<DeleteFileResponse> => {
    try {
        const url = `${BASE_URL}/file?path=${encodeURIComponent(request.path)}`;
        logger.log('[fileService]', 'Deleting file from path:', request.path);
        logger.log('[fileService]', 'Request URL for file deletion:', url);

        const response = await sentryFetch(
            url,
            {
                method: 'DELETE',
                headers: getHeadersFiles(),
            },
            {
                operation: 'delete_file',
                path: request.path
            }
        );

        if (!response.ok) {
            let errorResponseMessage = 'Error deleting file';
            try {
                const errorData = await response.json();
                if (errorData && errorData.message) {
                    errorResponseMessage = errorData.message;
                }
            } catch (parseError) {
                errorResponseMessage = response.statusText || 'Server returned an error without a JSON message';
                logger.warn('[fileService]', 'Could not parse error response as JSON for file deletion', parseError);
            }
            logger.error('[fileService]', 'Error deleting file:', {
                status: response.status,
                statusText: response.statusText,
                messageFromServer: errorResponseMessage,
                path: request.path
            });
            throw new Error(`Error: ${response.status} - ${errorResponseMessage}`);
        }

        const responseData = await response.json() as DeleteFileResponse;
        logger.log('[fileService]', 'File deletion successful. Server response:', responseData);
        return responseData;
    } catch (error) {
        logger.error('[fileService]', 'General error in deleteFile:', error);
        handleServiceError(error);
        throw error;
    }
};